import { Plus, X } from 'lucide-react';
import type { TabDoc } from '../hooks/useDocumentTabs';

interface EditorTabBarProps {
  tabs: TabDoc[];
  activeTabId: string | null;
  onSwitch: (id: string) => void;
  onClose: (id: string) => void;
  onNewTab: () => void;
}

/**
 * Row of open-document tabs rendered below the main toolbar. One tab per
 * open file, plus a `+` button at the end that opens a file picker (handled
 * by the parent). Clicking a tab switches to it; clicking its `X` closes it.
 */
export function EditorTabBar({
  tabs,
  activeTabId,
  onSwitch,
  onClose,
  onNewTab,
}: EditorTabBarProps) {
  return (
    <div
      className="flex items-stretch h-9 bg-background border-b border-border overflow-x-auto"
      role="tablist"
      aria-label="Open files"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        return (
          <div
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            className={`group flex items-center gap-2 px-3 border-r border-border max-w-[220px] cursor-pointer select-none ${
              isActive
                ? 'bg-accent text-foreground'
                : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
            }`}
            onClick={() => onSwitch(tab.id)}
            title={tab.fileName}
          >
            <span className="text-xs truncate">
              {tab.fileName}
              {tab.isDirty && <span className="ml-1 text-muted-foreground">*</span>}
            </span>
            <button
              type="button"
              aria-label={`Close ${tab.fileName}`}
              className="shrink-0 w-4 h-4 flex items-center justify-center rounded hover:bg-muted-foreground/20"
              onClick={(e) => {
                e.stopPropagation();
                onClose(tab.id);
              }}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        title="Open another file"
        aria-label="Open another file"
        className="shrink-0 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent/50"
        onClick={onNewTab}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
